import React, { useState, useEffect, useRef } from 'react';
import { Loader } from 'lucide-react';
import mammoth from 'mammoth';
import DOMPurify from 'dompurify';
import { tokenStorage } from '../utils/tokenStorage';
import { attemptSilentRefresh } from '../services/httpClient';
import { normalize, lcsMatch } from '../utils/textMatchUtils';

interface DocxRendererProps {
  /** URL to fetch the document from (authenticated via stored token). */
  documentUrl: string;
  /** Text to search for and highlight. */
  searchText?: string;
  /** Additional class names for the outer scroll container. */
  className?: string;
  /** Called after highlight attempt with (found, score). */
  onHighlightResult?: (found: boolean, score?: number) => void;
  /** Called once when the document finishes loading. */
  onLoadComplete?: () => void;
}

/** Collapse all whitespace to single spaces and lowercase. */
function collapseWhitespace(text: string): string {
  return text.replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * Renders .docx files by converting them to HTML via mammoth.js.
 * Follows the same auth/fetch pattern as PdfRenderer.
 */
export const DocxRenderer: React.FC<DocxRendererProps> = ({
  documentUrl,
  searchText,
  className = '',
  onHighlightResult,
  onLoadComplete,
}) => {
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // Refs to avoid callback identity in effect deps
  const onHighlightResultRef = useRef(onHighlightResult);
  onHighlightResultRef.current = onHighlightResult;
  const onLoadCompleteRef = useRef(onLoadComplete);
  onLoadCompleteRef.current = onLoadComplete;

  useEffect(() => {
    let cancelled = false;

    const loadDocx = async () => {
      try {
        setLoading(true);
        setError(null);
        setHtml(null);

        let token = tokenStorage.getToken();

        if (!token || tokenStorage.isTokenExpired(token)) {
          const refreshed = await attemptSilentRefresh();
          if (refreshed) token = tokenStorage.getToken();
        }

        let response = await fetch(documentUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
        });

        if (response.status === 401) {
          const refreshed = await attemptSilentRefresh();
          if (refreshed) {
            token = tokenStorage.getToken();
            response = await fetch(documentUrl, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
              credentials: 'include',
            });
          }
        }

        if (!response.ok) throw new Error(`Failed to fetch document: ${response.status}`);

        const arrayBuffer = await response.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });

        if (cancelled) return;

        setHtml(DOMPurify.sanitize(result.value));
        setLoading(false);
        onLoadCompleteRef.current?.();
      } catch {
        if (!cancelled) {
          setError('Failed to load document. Please try again.');
          setLoading(false);
        }
      }
    };

    loadDocx();
    return () => { cancelled = true; };
  }, [documentUrl]);

  // Highlight search text after HTML is rendered.
  // We operate on contentRef (the inner div with dangerouslySetInnerHTML)
  // and use onHighlightResultRef to avoid re-triggering on callback identity changes.
  useEffect(() => {
    if (!html || !searchText || !contentRef.current) {
      onHighlightResultRef.current?.(false, 0);
      return;
    }

    // Clean up any previous <mark> elements before searching
    const container = contentRef.current;
    container.querySelectorAll('mark[data-docx-highlight]').forEach((mark) => {
      const parent = mark.parentNode;
      if (parent) {
        while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
        parent.removeChild(mark);
        parent.normalize(); // merge adjacent text nodes back together
      }
    });

    const searchNorm = collapseWhitespace(searchText);
    if (searchNorm.length < 5) {
      onHighlightResultRef.current?.(false, 0);
      return;
    }

    // Build full text from the rendered HTML via TreeWalker.
    // Insert a space between text nodes from different block elements
    // so "Paul McGinn" in <p> + "DOB: 23 December" in next <p> becomes
    // "Paul McGinn DOB: 23 December" (not "Paul McGinnDOB: 23 December").
    const doc = container.ownerDocument;
    const walker = doc.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let fullText = '';
    const nodeOffsets: Array<{ node: Text; start: number; end: number }> = [];

    let node: Text | null;
    while ((node = walker.nextNode() as Text | null)) {
      const content = node.textContent || '';
      if (!content.trim()) continue;
      // Add a space separator if the previous chunk didn't end with whitespace
      if (fullText.length > 0 && !/\s$/.test(fullText)) {
        fullText += ' ';
      }
      const start = fullText.length;
      fullText += content;
      nodeOffsets.push({ node, start, end: fullText.length });
    }

    const fullTextNorm = collapseWhitespace(fullText);

    // --- Matching strategies (in order of preference) ---
    // Result: origStart/origEnd in original fullText coordinates, score 0–1
    let origStart = -1;
    let origEnd = -1;
    let score = 0;

    // Build collapseWhitespace position map (shared by strategies 1 & 2)
    const cwNormToOrig: number[] = [];
    {
      let prevWasSpace = false;
      const lower = fullText.toLowerCase();
      for (let i = 0; i < lower.length; i++) {
        if (/\s/.test(lower[i])) {
          if (!prevWasSpace) { cwNormToOrig.push(i); prevWasSpace = true; }
        } else {
          cwNormToOrig.push(i); prevWasSpace = false;
        }
      }
    }

    const cwToOrig = (normIdx: number) => cwNormToOrig[Math.min(normIdx, cwNormToOrig.length - 1)] ?? normIdx;

    // Strategy 1: Direct match on the full search text
    const directIdx = fullTextNorm.indexOf(searchNorm);
    if (directIdx !== -1) {
      origStart = cwToOrig(directIdx);
      origEnd = cwToOrig(directIdx + searchNorm.length);
      score = 1.0;
    }

    // Strategy 1b: Substring match — skip leading noise (e.g. LLM-added "1. ")
    // and try matching from the first alphabetic character onwards.
    if (origStart === -1 && searchNorm.length > 20) {
      const alphaIdx = searchNorm.search(/[a-z]/);
      if (alphaIdx > 0) {
        const trimmed = searchNorm.substring(alphaIdx);
        const trimIdx = fullTextNorm.indexOf(trimmed);
        if (trimIdx !== -1) {
          origStart = cwToOrig(trimIdx);
          origEnd = cwToOrig(trimIdx + trimmed.length);
          score = 0.95;
        }
      }
    }

    // Strategy 2: Ellipsis-split — LLM excerpts often join non-contiguous passages with "..."
    if (origStart === -1) {
      const segments = searchText
        .split(/\.{3,}|…/)
        .map(s => collapseWhitespace(s))
        .filter(s => s.length >= 10);

      if (segments.length > 0) {
        // Try each segment, also skipping leading noise
        for (const seg of segments) {
          let idx = fullTextNorm.indexOf(seg);
          if (idx === -1) {
            const ai = seg.search(/[a-z]/);
            if (ai > 0) idx = fullTextNorm.indexOf(seg.substring(ai));
          }
          if (idx !== -1) {
            origStart = cwToOrig(idx);
            origEnd = cwToOrig(idx + seg.length);
            score = 0.9;
            break;
          }
        }
      }
    }

    // Strategy 3: LCS sliding window — search the full document text
    // without truncation by sliding a window across it.
    if (origStart === -1) {
      const normSearch = normalize(searchText);
      const normFull = normalize(fullText);

      // Slide a window across normFull, computing LCS against normSearch
      const windowSize = 2000;
      const step = 1000; // 50% overlap
      let bestRatio = 0;
      let bestStart = -1;
      let bestEnd = -1;

      for (let offset = 0; offset < normFull.length; offset += step) {
        const window = normFull.slice(offset, offset + windowSize);
        const lcs = lcsMatch(normSearch, window);
        if (lcs.ratio > bestRatio && lcs.matchStart >= 0) {
          bestRatio = lcs.ratio;
          bestStart = offset + lcs.matchStart;
          bestEnd = offset + lcs.matchEnd;
        }
        // Early exit if we found an excellent match
        if (bestRatio >= 0.8) break;
      }

      if (bestRatio >= 0.25 && bestStart >= 0) {
        // Build normalize() position map: stripped-index → original fullText index
        const stripNormToOrig: number[] = [];
        const lower = fullText.toLowerCase();
        let prevWasSpace = false;
        for (let i = 0; i < lower.length; i++) {
          const ch = lower[i];
          if (/[^a-z0-9\s]/.test(ch)) continue; // stripped by normalize()
          if (/\s/.test(ch)) {
            if (!prevWasSpace) { stripNormToOrig.push(i); prevWasSpace = true; }
          } else {
            stripNormToOrig.push(i); prevWasSpace = false;
          }
        }

        origStart = stripNormToOrig[bestStart] ?? bestStart;
        origEnd = stripNormToOrig[Math.min(bestEnd, stripNormToOrig.length - 1)] ?? bestEnd;
        score = bestRatio;
      }
    }

    if (origStart === -1 || origEnd === -1) {
      onHighlightResultRef.current?.(false, 0);
      return;
    }

    // Find which text nodes overlap the match range and highlight them
    let highlighted = false;
    let firstMark: HTMLElement | null = null;

    for (const { node: textNode, start: nodeStart, end: nodeEnd } of nodeOffsets) {
      if (nodeEnd <= origStart || nodeStart >= origEnd) continue;

      const highlightStart = Math.max(0, origStart - nodeStart);
      const highlightEnd = Math.min(textNode.textContent!.length, origEnd - nodeStart);

      if (highlightStart >= highlightEnd) continue;

      try {
        const range = doc.createRange();
        range.setStart(textNode, highlightStart);
        range.setEnd(textNode, highlightEnd);

        const mark = doc.createElement('mark');
        mark.style.backgroundColor = 'rgba(255, 255, 0, 0.5)';
        mark.style.padding = '0';
        mark.setAttribute('data-docx-highlight', 'true');
        range.surroundContents(mark);

        if (!firstMark) firstMark = mark;
        highlighted = true;
      } catch {
        // surroundContents can fail if the range crosses element boundaries — skip
      }
    }

    if (firstMark) {
      setTimeout(() => {
        firstMark!.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }

    onHighlightResultRef.current?.(highlighted, highlighted ? score : 0);
  }, [html, searchText]);

  return (
    <div
      ref={containerRef}
      className={`overflow-auto bg-white dark:bg-medical-800 ${className}`}
    >
      {loading && (
        <div className="flex items-center justify-center h-full min-h-[200px]">
          <Loader className="h-8 w-8 animate-spin text-primary-600" />
          <span className="ml-2 text-medical-600 dark:text-medical-400">Loading document...</span>
        </div>
      )}

      {error && (
        <div className="flex items-center justify-center h-full min-h-[200px]">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {!loading && !error && html && (
        <div
          ref={contentRef}
          className="p-8 max-w-4xl mx-auto prose dark:prose-invert prose-sm"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
    </div>
  );
};
